import { z } from 'zod';
import type {
  AddEditClinicianRequestT,
  ClinicianRoleTypeT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import { dobField, npiField, phoneField, zipField } from '@/shared/lib/validators';

import type { PrescriberDetailsFormValues } from './types';

/** Valid clinician role type values. */
const clinicianRoleTypeSchema = z.enum(['1', '2', '3', '4', '5', '6']);

/**
 * Schema for AddEditClinicianRequestT with field-level validation messages.
 */
export const clinicianInfoSchema: z.ZodType<AddEditClinicianRequestT> = z.object({
  FirstName: z.string().trim().min(1, 'First name is required'),
  LastName: z.string().trim().min(1, 'Last name is required'),
  DateOfBirth: dobField,
  NPINumber: npiField,
  Address1: z
    .string()
    .trim()
    .min(1, 'Address is required')
    .max(35, 'Address must be 35 characters or fewer'),
  City: z.string().trim().min(1, 'City is required').max(35, 'City must be 35 characters or fewer'),
  State: z
    .string()
    .trim()
    .min(1, 'State is required')
    .max(2, 'State must be a 2-letter abbreviation'),
  ZipCode: zipField,
  PrimaryPhone: phoneField,
  PrimaryFax: phoneField,
  ClinicianRoleType: z
    .array(clinicianRoleTypeSchema)
    .min(1, 'At least one clinician role is required') as z.ZodType<ClinicianRoleTypeT[]>,
});

/**
 * Schema for the full prescriber details form values.
 */
export const prescriberDetailsFormSchema: z.ZodType<PrescriberDetailsFormValues> = z.object({
  clinicianInfo: clinicianInfoSchema,
});

/** Zod safe parse success result. */
export type ValidateDraftSuccess = { success: true; data: PrescriberDetailsFormValues };

/** Zod safe parse error result. */
export type ValidateDraftError = { success: false; error: z.ZodError<PrescriberDetailsFormValues> };

/** Zod safe parse result for validateDraft. */
export type ValidateDraftResult = ValidateDraftSuccess | ValidateDraftError;
